import React, {useEffect, useState} from 'react'
import {Button} from 'react-bootstrap'
import {useTranslation} from 'react-i18next'
import {InfoCircleFill} from 'react-bootstrap-icons';
import InputGroupWithMessages from "../../../../components/shared/inputgroupwithmessages/InputGroupWithMessages.js";
import {cloneDeep} from "lodash";
import {roleConstants, toastTypes} from "../../../../helpers/constants.js";
import SwitchGroup from "../../../shared/switchgroup/SwitchGroup.js";
import {getAirFlow, getReboot, getResetErrors, updateFieldIfChanged, validateParameter} from "../../../../helpers/awareSettingsHelper.js";
import {getParameterToShow} from "../../../../helpers/settingsHelper.js";
import {hasAnyRole} from "../../../../helpers/authHelper.js"
import PopOverDialog from "../../../../components/shared/popoverdialog/PopOverDialog.js";
import {awareParamNames} from "../../../parameters/awareParamSpec.js";
import {awareTelemNames} from "../../../parameters/awareTelemSpec.js";
import {paramNames} from "../../../parameters/parameterSpec.js";
import {addToast} from "../../../../helpers/reduxstore/reducers/toastReducer.js";
import {useDispatch} from "react-redux";
import useUserRole from "../../../../helpers/hooks/useUserRole.js";
import DeviceConfigTopInformation from "../../../shared/topinformation/DeviceConfigTopInformation.js";

/** Displays the configuration page for operational settings
 * @param {object} device device object
 * @param {string} className classes for the component wrapper
 * @param {function} save save function to trigger displaying group selector or confirmation (for single device)
 */
export default function AwareOperationSettings({device, className, save, showOnlyReboot}) {
    let disableAirflow = false
    let disableOverrideTime = false

    const {t} = useTranslation(['settingspage', 'dashboard', 'telemetry', 'common', 'locales'])
    const dispatch = useDispatch()
    const userRole = useUserRole();
    const [oldDeviceState, setOldDeviceState] = useState(cloneDeep(device))
    const [operationRequest, setOperationRequest] = useState({value: -1})
    const [airflow, setAirflow] = useState(getAirFlow(device, t))
    const [reboot, setReboot] = useState(getReboot(device, t))
    const [resetErrors, setResetErrors] = useState(getResetErrors(device, t))
    const [overrideTime, setOverrideTime] = useState(getParameterToShow(device, awareParamNames.manual_override_time, t))
    const systemMode = device?.latest_telemetry?.[awareTelemNames.op_mode]
    const connected = device?.connected

    const systemModeAllowedAirflowChange = [101, 102, 103, 104]

    if (systemModeAllowedAirflowChange.indexOf(systemMode) === -1) {
        disableAirflow = true
    }

    if (!connected) {
        disableAirflow = true
        disableOverrideTime = true
    }

    // When device is updated after write, update parameters to show / hide messages
    useEffect(() => {        
        const desiredOperationModeRequest = device.device_twin.desired?.parameters?.[awareTelemNames.op_mode]
        
        const messages = [{
            type: "warning",
            text: t('settingspage:setpoints_settings.messages.waiting_for_update')
        }]

        if (desiredOperationModeRequest && (operationRequest.value == -1 || desiredOperationModeRequest == operationRequest.value)) {
            setOperationRequest({value: desiredOperationModeRequest, messages: messages});
        }

        updateFieldIfChanged(setAirflow, oldDeviceState, device, "udf", t)
        updateFieldIfChanged(setOverrideTime, oldDeviceState, device, awareParamNames.manual_override_time, t)
        updateFieldIfChanged(setReboot, oldDeviceState, device, "reboot", t)
        updateFieldIfChanged(setResetErrors, oldDeviceState, device, "reset_errors", t)
        setOldDeviceState(cloneDeep(device))
    }, [device])

    /**
     * validates parameters. Adds messages to viewmodels if fail. returns true if all is validated, otherwise false.
     * @returns {boolean}
     */


    const validateParameters = () => {
        let validatedList = []
        if(airflow.value !== undefined) {
            validatedList.push(validateParameter("udf", airflow, setAirflow, t))
        }
        validatedList.push(validateParameter(awareParamNames.manual_override_time, overrideTime, setOverrideTime, t))
        return validatedList.every(v => v)
    }

    const getValuesToSave = () => {
        const updated_parameters = {}
        if (operationRequest.value !== -1) {
            updated_parameters[awareTelemNames.op_mode] = operationRequest.value
        }

        const desiredReboot = device.device_twin.desired.reboot
        if(reboot.value && desiredReboot != reboot.value) {
            updated_parameters[paramNames.reboot] = "1"
        }

        const desiredResetErrors = device.device_twin.desired.reset_errors
        if(resetErrors.value && desiredResetErrors != resetErrors.value) {
            updated_parameters["reset_errors"] = "1"
        }
       
        if(airflow.value !== undefined) {
            const reportedUdf = device?.latest_telemetry?.["udf"]
            if (reportedUdf != airflow.value) {
                if (device.device_twin.desired.parameters["udf"] && device.device_twin.desired.parameters["udf"] === airflow.value) {
                    console.log(` desired exists and is same for set_air_flow_request: ${device.device_twin.desired.parameters["udf"]}`)
                } else {
                    updated_parameters["udf"] = parseFloat(airflow.value)
                }
            }
        }

        const reportedManualOverrideTime = device.device_twin.reported.parameters[awareParamNames.manual_override_time]
        if (reportedManualOverrideTime != overrideTime.value) {
            if (device.device_twin.desired.parameters && device.device_twin.desired.parameters[awareParamNames.manual_override_time] && device.device_twin.desired.parameters[awareParamNames.manual_override_time] === overrideTime.value) {
                console.log(` desired exists and is same for ${awareParamNames.manual_override_time}: ${device.device_twin.desired.parameters[awareParamNames.manual_override_time]}`)
            } else {
                updated_parameters[awareParamNames.manual_override_time] = parseInt(overrideTime.value)
            }
        }
        
        return updated_parameters
    }

    function saveIfValidated(saveForGroup) {
        if (!validateParameters()) return

        let valuesToSave = getValuesToSave();
        if (Object.keys(valuesToSave).length === 0) {
            dispatch(addToast({
                id: "OperationSettingsInfo",
                type: toastTypes.info,
                title: "No changes",
                body: "No changes to parameters",
                timeSeconds: 3
            }))
            return
        }
        save(valuesToSave, saveForGroup);
    }

    return (
        <div className={className}>
                <DeviceConfigTopInformation className={'d-xs-none'} title={t('settingspage:operation_settings.title')}
                                    subTitle={t('settingspage:operation_settings.description_aware')}/>
            {showOnlyReboot ? (
                <div className="mt-sm-4 d-flex flex-wrap">
                    <div className="settings-item-container">
                        <SwitchGroup
                            label={t('settingspage:parameters.reboot')}
                            className='p-4 w-200px m-auto'
                            checked={reboot.value}
                            onChange={(e) => setReboot({value: e.target.checked ? true : false})}
                            messages={reboot.messages}
                            infoelement={<PopOverDialog popOverTitel={t('settingspage:parameters.reboot')}
                                                        popOverContent={t('locales:device_operation_reboot_device_question_mark')}
                                                        trigger={['hover', 'touch']}
                                                        contentAsHtml={true}/>} />
                    </div>
                </div>
            ) : (
                <>
                    <div className="mt-sm-4 d-flex flex-wrap">
                        {hasAnyRole(userRole, [roleConstants.Admin, roleConstants.Technical, roleConstants.TechnicalBasic]) && (
                            <div className="settings-item-container">
                                <SwitchGroup
                                    label={t('settingspage:parameters.reboot')}
                                    className='p-4 w-200px m-auto'
                                    checked={reboot.value}
                                    onChange={(e) => setReboot({value: e.target.checked ? true : false})}
                                    messages={reboot.messages}
                                    infoelement={<PopOverDialog popOverTitel={t('settingspage:parameters.reboot')}
                                                                popOverContent={t('locales:device_operation_reboot_device_question_mark')}
                                                                trigger={['hover', 'touch']}
                                                                contentAsHtml={true}/>} />
                            </div>
                        )}

                        {hasAnyRole(userRole, [roleConstants.Admin, roleConstants.Technical, roleConstants.TechnicalBasic]) && (
                            <div className="settings-item-container">
                                <SwitchGroup
                                    label={t('settingspage:parameters.reset_errors')}
                                    className='p-4 w-200px m-auto'
                                    checked={resetErrors.value}
                                    onChange={(e) => setResetErrors({value: e.target.checked ? true : false})}
                                    messages={resetErrors.messages}
                                    infoelement={<PopOverDialog popOverTitel={t('settingspage:parameters.reset_errors')}
                                                                popOverContent={t('locales:device_operation_reset_errors_question_mark')}
                                                                trigger={['hover', 'touch']}
                                                                contentAsHtml={true}/>} />
                            </div>
                        )}
                    </div>
                </>
            )}

            <div className="alert alert-info" hidden={!device.slave}>
                <InfoCircleFill className='me-1 mb-1 text-primary'/>{t('settingspage:slave-device-settings')}
                {device.master_serial_number && (
                    <a href={"/device/" + device.master_serial_number + "/configurations/operation"}>{device.master_serial_number}</a>
                )}
            </div>

            <div className='d-inline-block float-end p-2'>
                <Button variant='outline-secondary' onClick={() => saveIfValidated(false)} className='me-2'>
                    {t('settingspage:save_device')}
                </Button>
            </div>
        </div>
    );
}